import { useEffect, useState } from "react";
import { LifeBuoy, Send } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDateTime } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function AdminTickets() {
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState("");
  const [messages, setMessages] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => appApi.get("admin/tickets").then((d) => setTickets(d.tickets || [])).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const reply = async (t) => {
    const message = (messages[t.id] || "").trim();
    if (!message) {
      toast.error("Write a reply first.");
      return;
    }
    setBusy(t.id);
    try {
      await appApi.post(`admin/tickets/${t.id}/reply`, { message });
      toast.success(`Replied to ${t.reference_no}`);
      setMessages((m) => ({ ...m, [t.id]: "" }));
      setOpen("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-tickets-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Support Tickets</h1>
      </div>

      <div className="space-y-3" data-testid="admin-tickets-list">
        {(tickets || []).map((t) => (
          <div key={t.id} className="border-2 border-obsidian bg-white p-5" data-testid={`admin-ticket-${t.reference_no}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><LifeBuoy className="w-5 h-5" /></span>
                <div>
                  <p className="font-bold text-sm">{t.subject}</p>
                  <p className="text-xs font-mono text-zinc-500 mt-0.5">
                    {t.reference_no} · {t.merchant_name} ({t.merchant_email}) · {t.category?.toUpperCase()} · {fmtDateTime(t.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={t.status} />
                <button onClick={() => setOpen(open === t.id ? "" : t.id)} data-testid={`ticket-reply-toggle-${t.id}`}
                  className="text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-3 py-1.5 hover:bg-acid hover:text-white transition-colors duration-150">
                  Reply
                </button>
              </div>
            </div>
            <p className="text-sm text-zinc-600 mt-3 whitespace-pre-line">{t.description}</p>
            {open === t.id && (
              <div className="flex gap-3 mt-4">
                <input
                  value={messages[t.id] || ""}
                  onChange={(e) => setMessages((m) => ({ ...m, [t.id]: e.target.value }))}
                  placeholder="Reply to merchant…"
                  data-testid={`ticket-reply-input-${t.id}`}
                  className="flex-1 border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
                />
                <button onClick={() => reply(t)} disabled={busy === t.id} data-testid={`ticket-reply-send-${t.id}`}
                  className="inline-flex items-center gap-1.5 bg-obsidian text-sky-300 text-xs font-bold uppercase tracking-widest px-5 py-2.5 border-2 border-obsidian hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-60">
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </div>
            )}
          </div>
        ))}
        {tickets && tickets.length === 0 && <p className="text-sm text-zinc-500" data-testid="admin-tickets-empty">No tickets — inbox zero.</p>}
        {!tickets && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading tickets…</p>}
      </div>
    </div>
  );
}
